/**
 * Performance Monitoring System
 *
 * Tracks Web Vitals (CLS, FID, LCP, FCP, TTFB) and custom performance metrics.
 * Provides analytics integration hooks and performance reporting.
 */

export interface PerformanceMetric {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta?: number;
  id?: string;
  navigationType?: string;
}

export interface CustomMetric {
  name: string;
  value: number;
  timestamp: number;
  metadata?: Record<string, unknown>;
}

export interface PerformanceReport {
  metrics: PerformanceMetric[];
  customMetrics: CustomMetric[];
  timestamp: number;
  url: string;
  userAgent: string;
}

interface LayoutShiftEntry extends PerformanceEntry {
  hadRecentInput?: boolean;
  value: number;
}

interface PerformanceEventTimingEntry extends PerformanceEntry {
  processingStart: number;
}

type PerformanceCallback = (metric: PerformanceMetric) => void;
type ReportCallback = (report: PerformanceReport) => void;

class PerformanceMonitor {
  private metrics: PerformanceMetric[] = [];
  private customMetrics: CustomMetric[] = [];
  private callbacks: PerformanceCallback[] = [];
  private reportCallbacks: ReportCallback[] = [];
  private isInitialized = false;

  /**
   * Initialize performance monitoring
   */
  initialize(): void {
    if (this.isInitialized) {
      console.warn('PerformanceMonitor already initialized');
      return;
    }

    this.isInitialized = true;

    // Track Web Vitals if available
    if (typeof window !== 'undefined' && 'performance' in window) {
      this.trackWebVitals();
      this.trackNavigationTiming();
      this.trackResourceTiming();
    }
  }

  /**
   * Track Web Vitals (Core Web Vitals)
   */
  private trackWebVitals(): void {
    // FCP - First Contentful Paint
    this.observePaint('first-contentful-paint', 'FCP');

    // LCP - Largest Contentful Paint
    this.observeLargestContentfulPaint();

    // CLS - Cumulative Layout Shift
    this.observeLayoutShift();

    // FID - First Input Delay (replaced by INP in future)
    this.observeFirstInput();

    // TTFB - Time to First Byte
    this.trackTTFB();
  }

  /**
   * Observe paint timing
   */
  private observePaint(paintType: string, metricName: string): void {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.name === paintType) {
          const value = entry.startTime;
          this.recordMetric({
            name: metricName,
            value,
            rating: this.getRating(metricName, value),
          });
        }
      }
    });

    try {
      observer.observe({ type: 'paint', buffered: true });
    } catch (e) {
      console.error(JSON.stringify(e));
    }
  }

  /**
   * Observe Largest Contentful Paint
   */
  private observeLargestContentfulPaint(): void {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];

      if (lastEntry) {
        const value = lastEntry.startTime;
        this.recordMetric({
          name: 'LCP',
          value,
          rating: this.getRating('LCP', value),
        });
      }
    });

    try {
      observer.observe({ type: 'largest-contentful-paint', buffered: true });
    } catch (e) {
      console.error(JSON.stringify(e));
    }
  }

  private observeLayoutShift(): void {
    let clsValue = 0;

    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        const shift = entry as LayoutShiftEntry;
        if (!shift.hadRecentInput) {
          clsValue += shift.value;
        }
      }

      this.recordMetric({
        name: 'CLS',
        value: clsValue,
        rating: this.getRating('CLS', clsValue),
      });
    });

    try {
      observer.observe({ type: 'layout-shift', buffered: true });
    } catch (e) {
      console.error(JSON.stringify(e));
    }
  }

  /**
   * Observe First Input Delay
   */
  private observeFirstInput(): void {
    const observer = new PerformanceObserver((list) => {
      const firstInput = list.getEntries()[0];

      if (firstInput) {
        const timing = firstInput as PerformanceEventTimingEntry;
        const value = timing.processingStart - firstInput.startTime;
        this.recordMetric({
          name: 'FID',
          value,
          rating: this.getRating('FID', value),
        });
      }
    });

    try {
      observer.observe({ type: 'first-input', buffered: true });
    } catch (e) {
      console.error(JSON.stringify(e));
    }
  }

  /**
   * Track Time to First Byte
   */
  private trackTTFB(): void {
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;

    if (navigation) {
      const value = navigation.responseStart - navigation.requestStart;
      this.recordMetric({
        name: 'TTFB',
        value,
        rating: this.getRating('TTFB', value),
      });
    }
  }

  /**
   * Track Navigation Timing
   */
  private trackNavigationTiming(): void {
    window.addEventListener('load', () => {
      const navigation = performance.getEntriesByType(
        'navigation'
      )[0] as PerformanceNavigationTiming;

      if (navigation) {
        // DOM Interactive
        this.recordCustomMetric('DOM Interactive', navigation.domInteractive);

        // DOM Complete
        this.recordCustomMetric('DOM Complete', navigation.domComplete);

        // Load Complete
        this.recordCustomMetric('Load Complete', navigation.loadEventEnd);
      }
    });
  }

  /**
   * Track Resource Timing
   */
  private trackResourceTiming(): void {
    window.addEventListener('load', () => {
      const resources = performance.getEntriesByType('resource');

      const jsResources = resources.filter((r) => r.name.endsWith('.js'));
      const cssResources = resources.filter((r) => r.name.endsWith('.css'));
      const imgResources = resources.filter((r) => r.name.match(/\.(jpg|jpeg|png|gif|svg|webp)$/i));

      this.recordCustomMetric('JS Resources Count', jsResources.length);
      this.recordCustomMetric('CSS Resources Count', cssResources.length);
      this.recordCustomMetric('Image Resources Count', imgResources.length);

      const totalDuration = resources.reduce((sum, r) => sum + r.duration, 0);
      this.recordCustomMetric('Total Resource Load Time', totalDuration);
    });
  }

  /**
   * Record a performance metric
   */
  private recordMetric(metric: PerformanceMetric): void {
    this.metrics.push(metric);
    this.callbacks.forEach((callback) => callback(metric));

    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console -- dev-only performance instrumentation
      console.log(`[Performance] ${metric.name}:`, metric.value, `(${metric.rating})`);
    }
  }

  /**
   * Record a custom metric
   */
  recordCustomMetric(name: string, value: number, metadata?: Record<string, unknown>): void {
    const metric: CustomMetric = {
      name,
      value,
      timestamp: Date.now(),
      metadata,
    };

    this.customMetrics.push(metric);

    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console -- dev-only performance instrumentation
      console.log(`[Performance] Custom: ${name}:`, value);
    }
  }

  /**
   * Track custom timing
   */
  startTiming(name: string): () => void {
    const start = performance.now();

    return () => {
      const duration = performance.now() - start;
      this.recordCustomMetric(name, duration);
    };
  }

  /**
   * Get performance rating based on thresholds
   */
  private getRating(metricName: string, value: number): 'good' | 'needs-improvement' | 'poor' {
    const thresholds: Record<string, [number, number]> = {
      FCP: [1800, 3000],
      LCP: [2500, 4000],
      CLS: [0.1, 0.25],
      FID: [100, 300],
      TTFB: [800, 1800],
    };

    const [good, poor] = thresholds[metricName] || [0, 0];

    if (value <= good) return 'good';
    if (value <= poor) return 'needs-improvement';
    return 'poor';
  }

  /**
   * Subscribe to performance metrics
   */
  onMetric(callback: PerformanceCallback): () => void {
    this.callbacks.push(callback);

    // Return unsubscribe function
    return () => {
      const index = this.callbacks.indexOf(callback);
      if (index > -1) {
        this.callbacks.splice(index, 1);
      }
    };
  }

  /**
   * Subscribe to performance reports
   */
  onReport(callback: ReportCallback): () => void {
    this.reportCallbacks.push(callback);

    // Return unsubscribe function
    return () => {
      const index = this.reportCallbacks.indexOf(callback);
      if (index > -1) {
        this.reportCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Generate performance report
   */
  getReport(): PerformanceReport {
    return {
      metrics: [...this.metrics],
      customMetrics: [...this.customMetrics],
      timestamp: Date.now(),
      url: window.location.href,
      userAgent: navigator.userAgent,
    };
  }

  /**
   * Send report to analytics endpoint
   */
  async sendReport(endpoint?: string): Promise<void> {
    const report = this.getReport();

    // Call report callbacks
    this.reportCallbacks.forEach((callback) => callback(report));

    // Send to analytics endpoint if provided
    if (endpoint) {
      try {
        await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(report),
        });
      } catch (error) {
        console.error('Failed to send performance report:', error);
      }
    }
  }

  /**
   * Clear all metrics
   */
  clear(): void {
    this.metrics = [];
    this.customMetrics = [];
  }

  /**
   * Get all metrics
   */
  getMetrics(): PerformanceMetric[] {
    return [...this.metrics];
  }

  /**
   * Get all custom metrics
   */
  getCustomMetrics(): CustomMetric[] {
    return [...this.customMetrics];
  }
}

// Singleton instance
export const performanceMonitor = new PerformanceMonitor();

// Auto-initialize in browser
if (typeof window !== 'undefined') {
  performanceMonitor.initialize();
}

// Export convenience functions
export const recordCustomMetric = (
  name: string,
  value: number,
  metadata?: Record<string, unknown>
) => performanceMonitor.recordCustomMetric(name, value, metadata);

export const startTiming = (name: string) => performanceMonitor.startTiming(name);

export const onPerformanceMetric = (callback: PerformanceCallback) =>
  performanceMonitor.onMetric(callback);

export const getPerformanceReport = () => performanceMonitor.getReport();

export const sendPerformanceReport = (endpoint?: string) => performanceMonitor.sendReport(endpoint);
